import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { AlertCircle, ArrowLeft, ArrowRight, ChevronsLeft, ChevronsRight, Search } from 'lucide-react'
import * as React from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  searchKey?: string
  searchPlaceholder?: string
  onRowClick?: (data: TData) => void
  pageCount?: number
  pagination?: PaginationState
  onPaginationChange?: React.Dispatch<React.SetStateAction<PaginationState>>
  totalRecords?: number
  rowSelection?: Record<string, boolean>
  onRowSelectionChange?: (selection: Record<string, boolean>) => void
  getRowId?: (originalRow: TData, index: number, parent?: any) => string
  groupKey?: (data: TData) => string
  groupCells?: string[]
  globalFilter?: string
  onGlobalFilterChange?: (value: string) => void
  globalFilterColumns?: string[]
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchKey,
  searchPlaceholder = "Buscar...",
  onRowClick,
  pageCount,
  pagination,
  onPaginationChange,
  totalRecords,
  rowSelection: externalRowSelection,
  onRowSelectionChange,
  getRowId,
  groupKey,
  groupCells,
  globalFilter,
  onGlobalFilterChange,
  globalFilterColumns,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [internalRowSelection, setInternalRowSelection] = React.useState({})
  const isControlled = externalRowSelection !== undefined
  const rowSelection = isControlled ? externalRowSelection : internalRowSelection
  const setRowSelection = (updater: React.SetStateAction<Record<string, boolean>>) => {
    if (isControlled) {
      const next = typeof updater === 'function' ? updater(externalRowSelection) : updater
      onRowSelectionChange?.(next)
    } else {
      setInternalRowSelection(updater)
    }
  }
  const scrollPositionRef = React.useRef(0)
  const tableBodyRef = React.useRef<HTMLDivElement>(null)

  const handlePageChange = (newPageIndex: number) => {
    const container = tableBodyRef.current
    if (container) {
      scrollPositionRef.current = container.scrollTop
    }
    onPaginationChange?.({ pageIndex: newPageIndex, pageSize: pagination?.pageSize ?? 10 })
  }

  React.useEffect(() => {
    const container = tableBodyRef.current
    if (container && scrollPositionRef.current !== 0) {
      container.scrollTop = scrollPositionRef.current
    }
  }, [data])

  const renderPaginationButtons = () => (
    <div className="flex items-center space-x-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(0)}
        disabled={!table.getCanPreviousPage()}
        className="h-8 p-2"
      >
        <ChevronsLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(table.getState().pagination.pageIndex - 1)}
        disabled={!table.getCanPreviousPage()}
        className="h-8 p-2"
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(table.getState().pagination.pageIndex + 1)}
        disabled={!table.getCanNextPage()}
        className="h-8 p-2"
      >
        <ArrowRight className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(table.getPageCount() - 1)}
        disabled={!table.getCanNextPage()}
        className="h-8 p-2"
      >
        <ChevronsRight className="h-4 w-4" />
      </Button>
    </div>
  )

  const table = useReactTable({
    data,
    columns,
    getRowId,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: pagination && getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: !!onPaginationChange,
    pageCount: pageCount ?? -1,
    enableGlobalFilter: !!onGlobalFilterChange,
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = String(filterValue ?? '').toLowerCase().trim()
      if (!search) return true
      if (!globalFilterColumns || globalFilterColumns.length === 0) return true
      return globalFilterColumns.some(colId => {
        const value = row.getValue(colId)
        return String(value ?? '').toLowerCase().includes(search)
      })
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(pagination !== undefined ? { pagination } : {}),
      ...(globalFilter !== undefined ? { globalFilter } : {}),
    },
    onPaginationChange: onPaginationChange,
    onGlobalFilterChange: onGlobalFilterChange,
  })

  const renderPagination = (isTop: boolean = false) => {
    const pageIndex = table.getState().pagination.pageIndex
    const pageSize = table.getState().pagination.pageSize
    const displayTotalRecords = totalRecords ?? table.getFilteredRowModel().rows.length
    const startRecord = displayTotalRecords > 0 ? pageIndex * pageSize + 1 : 0
    const endRecord = Math.min((pageIndex + 1) * pageSize, displayTotalRecords)
    const totalPageCount = table.getPageCount() <= 0 ? 1 : table.getPageCount()

    return (
      <div className="flex items-center justify-between py-4">
        {!isTop ? <div className="flex-1 text-sm text-muted-foreground font-medium">
          {table.getFilteredSelectedRowModel().rows.length > 0 ? (
            <>
              <span className="text-foreground">{table.getFilteredSelectedRowModel().rows.length}</span> de{" "}
              <span className="text-foreground">{displayTotalRecords}</span> registro(s) seleccionado(s).
            </>
          ) : displayTotalRecords > 0 ? (
            <>
              Mostrando <span className="text-foreground">{startRecord}-{endRecord}</span> de <span className="text-foreground">{displayTotalRecords}</span> registros
            </>
          ) : (
            <span className="text-xs font-medium uppercase tracking-wider opacity-50">
              0 Registros totales
            </span>
          )}
        </div> : <div className=""></div>}
        <div className="flex items-center gap-x-6">
          <div className="text-sm font-medium text-muted-foreground font-mono">
            <span className='hidden sm:inline'>Página</span>
            <span className='sm:hidden'>Pág.</span>
            {" "}
            <span className="text-foreground">{pageIndex + 1}</span>
            {" "}de{" "}
            <span className="text-foreground">{totalPageCount}</span>
          </div>
          {renderPaginationButtons()}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Search and filters */}
      <div className="flex items-center justify-between">
        {searchKey ? (
          <div className="relative w-full sm:max-w-sm py-4">
            <Search className="absolute left-2 top-6.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={(table.getColumn(searchKey)?.getFilterValue() as string) ?? ""}
              onChange={(event) =>
                table.getColumn(searchKey)?.setFilterValue(event.target.value)
              }
              className="pl-8"
            />
          </div>
        ) : (
          <div />
        )}
        {onGlobalFilterChange && (
          <div className="relative w-full sm:max-w-sm py-4">
            <Search className="absolute left-2 top-6.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar..."
              value={globalFilter ?? ""}
              onChange={(event) => onGlobalFilterChange(event.target.value)}
              className="pl-8"
            />
          </div>
        )}
        <div className="flex-1 ml-4 sm:block hidden">
          {pagination && renderPagination(!!searchKey || !!onGlobalFilterChange)}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className='px-3'>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (() => {
              const groupMap = new Map<string, { first: number; last: number }>()
              table.getRowModel().rows.forEach((row, idx) => {
                if (groupKey) {
                  const key = groupKey(row.original)
                  const existing = groupMap.get(key)
                  if (!existing) {
                    groupMap.set(key, { first: idx, last: idx })
                  } else {
                    existing.last = idx
                  }
                }
              })
              const lastInGroup = new Set<number>()
              const firstInGroup = new Set<number>()
              groupMap.forEach(({ first, last }) => {
                firstInGroup.add(first)
                lastInGroup.add(last)
              })

              return table.getRowModel().rows.map((row, idx) => {
                const isLastInGroup = lastInGroup.has(idx)
                const isFirstInGroup = firstInGroup.has(idx)
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => onRowClick?.(row.original)}
                    className={`${onRowClick ? 'cursor-pointer hover:bg-accent transition-colors' : ''} ${isLastInGroup ? 'border-b border-border' : ''}`}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const colId = cell.column.id
                      const shouldHide = groupCells?.includes(colId) && !isFirstInGroup
                      if (shouldHide) {
                        return <TableCell key={cell.id} className='py-2 px-3' />
                      }
                      return (
                        <TableCell key={cell.id} className='py-2 px-3'>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })
            })() : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <Badge variant="outline">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      Sin resultados
                    </div>
                  </Badge>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && renderPagination()}
    </div>
  )
}
